import PropTypes from 'prop-types';
import React from 'react';
import { Button, Icon } from '@upsales/components';
import CriteriaDrilldownWrapper from '../../CriteriaDrilldownWrapper';
import NumberInput from '../Number';
import List from '../List';
import TextInput from '../Text';
import Bem from '@upsales/components/Utils/bemClass';
import { cloneDeep } from 'lodash';
import T from 'Components/Helpers/translate';
import { initSelection } from 'App/babel/filterConfigs/Prospecting/Location';

import './Address.scss';

export default class AddressDrilldown extends React.Component {
	constructor(props) {
		super(props);

		this.lang = {
			choose: T('default.choose'),
			state: T('address.state'),
			municipality: T('address.municipality'),
			town: T('address.town'),
			street: T('address.streetAddress'),
			statePlaceholder: T('default.choose') + ' ' + T('address.state').toLowerCase(),
			municipalityPlaceholder: T('default.choose') + ' ' + T('address.municipality').toLowerCase(),
			townPlaceholder: T('default.choose') + ' ' + T('address.town').toLowerCase(),
			streetPlaceholder: T('prospecting.filters.enterAStreetName'),
			zipCode: T('address.zip'),
			closed: T('default.closed'),
			open: T('default.open')
		};

		this.onStateChange = this.onListChange.bind(this, 'state');
		this.onMunicipalityChange = this.onListChange.bind(this, 'municipality');
		this.onTownChange = this.onListChange.bind(this, 'town');
		this.onAddressChange = this.onListChange.bind(this, 'address');

		const searchFunctions = this.props.filterConfigs.Location.searchFunctions;
		this.useCascade = !!searchFunctions.municipalityCascadeSearchFn;
		this.stateSerchFn = searchFunctions.stateSerchFn;
		// Cascade Kommun on Län, Ort on Län+Kommun. Closures read the latest
		// filter values at call time so each search reflects the current
		// parent selections without having to rewire the searchFn config.
		this.municipalitySerchFn = this.useCascade
			? searchFunctions.municipalityCascadeSearchFn(() => this.props.filter.value.state.value)
			: searchFunctions.municipalitySerchFn;
		this.townSerchFn = this.useCascade
			? searchFunctions.townCascadeSearchFn(
					() => this.props.filter.value.state.value,
					() => this.props.filter.value.municipality.value
			  )
			: searchFunctions.townSerchFn;
	}

	onListChange = (key, _, value) => {
		const { filter, onChange } = this.props;
		const newFilter = cloneDeep(filter);
		newFilter.value[key] = value;

		// Cascading filter: changing a parent invalidates child selections,
		// otherwise stale picks silently filter the result to zero matches.
		if (this.useCascade) {
			if (key === 'state') {
				newFilter.value.municipality = { ...newFilter.value.municipality, value: [] };
				newFilter.value.town = { ...newFilter.value.town, value: [] };
			} else if (key === 'municipality') {
				newFilter.value.town = { ...newFilter.value.town, value: [] };
			}
		}

		onChange(newFilter.filterName, newFilter);
	};

	addPostalCode = () => {
		const { filter, onChange } = this.props;
		const newFilter = cloneDeep(filter);
		const maxKey = newFilter.value.zipcode.value.reduce((max, value) => (value.key > max ? value.key : max), 0);
		newFilter.value.zipcode.value.push({ start: null, end: null, key: maxKey + 1 });
		onChange(newFilter.filterName, newFilter);
	};

	removePostalCode = key => {
		const { filter, onChange } = this.props;
		const newFilter = cloneDeep(filter);
		const index = newFilter.value.zipcode.value.findIndex(value => value.key === key);
		newFilter.value.zipcode.value.splice(index, 1);
		onChange(newFilter.filterName, newFilter);
	};

	onZipCodeChanged(key, value) {
		const { filter, onChange } = this.props;
		const newFilter = cloneDeep(filter);
		const index = newFilter.value.zipcode.value.findIndex(value => value.key === key);
		newFilter.value.zipcode.value[index] = value;
		newFilter.value.zipcode.value[index].key = key;
		onChange(newFilter.filterName, newFilter);
	}

	render() {
		const { filter, open, selectData } = this.props;

		const stateSelect = {
			config: {
				filterName: 'State',
				type: 'list',
				searchFn: this.stateSerchFn,
				resource: initSelection,
				placeholder: this.lang.statePlaceholder
			},
			filter: filter.value.state,
			onChange: this.onStateChange,
			selectData: { ...selectData[filter.filterName] }
		};

		// Cascading lists must skip the prefetched dataPromise cache, otherwise
		// the opened dropdown would replay the unfiltered initial fetch instead
		// of showing kommuner/orter narrowed by the current parent.
		const municipalitySelect = {
			config: {
				filterName: 'Municipality',
				type: 'list',
				searchFn: this.municipalitySerchFn,
				resource: initSelection,
				placeholder: this.lang.municipalityPlaceholder
			},
			filter: filter.value.municipality,
			onChange: this.onMunicipalityChange,
			selectData: this.useCascade ? {} : { ...selectData[filter.filterName] }
		};

		const townSelect = {
			config: {
				filterName: 'Town',
				type: 'list',
				searchFn: this.townSerchFn,
				resource: initSelection,
				placeholder: this.lang.townPlaceholder
			},
			filter: filter.value.town,
			onChange: this.onTownChange,
			selectData: this.useCascade ? {} : { ...selectData[filter.filterName] }
		};

		// select2 caches its config in componentDidMount; force a fresh
		// instance when the parent selection changes so the new searchFn
		// (with updated cascade filters) is picked up and any auto-cleared
		// child selections drop out of the underlying select2 widget.
		// JSON.stringify avoids ambiguity if a value contains the delimiter.
		// Without the cascade the keys stay constant, so no remounts happen.
		const stateKey = this.useCascade ? JSON.stringify(filter.value.state.value) : '';
		const municipalityKey = this.useCascade ? JSON.stringify(filter.value.municipality.value) : '';
		const municipalityListKey = `municipality-${stateKey}`;
		const townListKey = `town-${stateKey}-${municipalityKey}`;

		const streetSelect = {
			config: {
				filterName: 'Street',
				type: 'list',
				displayType: 'text',
				placeholder: this.lang.streetPlaceholder
			},
			filter: filter.value.address,
			onChange: this.onAddressChange,
			selectData: {},
			autoFocus: false
		};

		const classes = new Bem('ProspectingDrilldown');

		const zipCodeRows = filter.value.zipcode.value.map((value, index, array) => {
			const lastChild = index === array.length - 1;
			let button = null;

			if (lastChild) {
				button = (
					<Button type="link" onClick={this.addPostalCode}>
						<Icon name="plus" color="bright-blue" />
					</Button>
				);
			} else {
				button = (
					<Button type="link" onClick={this.removePostalCode.bind(this, value.key)}>
						<Icon name="trash" color="grey-10" />
					</Button>
				);
			}

			return (
				<tr key={value.key}>
					<td className="title-col">
						{index === 0 ? <div className="drilldown-row">{this.lang.zipCode}</div> : null}
					</td>
					<td className="table-col">
						<div className="flex-row">
							<div className="drilldown-row">
								<NumberInput
									space={true}
									filter={{ value }}
									onChange={this.onZipCodeChanged.bind(this, value.key)}
									autoFocus={false}
									format="postalCode"
								/>
								{button}
							</div>
						</div>
					</td>
					<td className="remove-filter-col" />
				</tr>
			);
		});

		return (
			<CriteriaDrilldownWrapper
				key={filter.filterName + '-drilldown'}
				className={classes.mod('addresses').b()}
				open={open}
			>
				<td colSpan={3}>
					<div className="drilldown-animation-wrapper">
						<table cellPadding={5}>
							<tbody>
								{/* State row */}
								<tr>
									<td className="title-col">
										<div className="drilldown-row">{this.lang.state}</div>
									</td>
									<td className="table-col">
										<div className="flex-row">
											<div className="drilldown-row">
												<List {...stateSelect} />
											</div>
										</div>
									</td>
									<td className="remove-filter-col" />
								</tr>

								{/* Municipality row */}
								<tr>
									<td className="title-col">
										<div className="drilldown-row">{this.lang.municipality}</div>
									</td>
									<td className="table-col">
										<div className="flex-row">
											<div className="drilldown-row">
												<List key={municipalityListKey} {...municipalitySelect} />
											</div>
										</div>
									</td>
									<td className="remove-filter-col" />
								</tr>

								{/* Town row */}
								<tr>
									<td className="title-col">
										<div className="drilldown-row">{this.lang.town}</div>
									</td>
									<td className="table-col">
										<div className="flex-row">
											<div className="drilldown-row">
												<List key={townListKey} {...townSelect} />
											</div>
										</div>
									</td>
									<td className="remove-filter-col" />
								</tr>

								{/* State rows */}
								{zipCodeRows}

								{/* Street row */}
								<tr>
									<td className="title-col">
										<div className="drilldown-row">{this.lang.street}</div>
									</td>
									<td className="table-col">
										<div className="flex-row">
											<div className="drilldown-row">
												<TextInput {...streetSelect} />
											</div>
										</div>
									</td>
									<td className="remove-filter-col" />
								</tr>
							</tbody>
						</table>
					</div>
				</td>
			</CriteriaDrilldownWrapper>
		);
	}
}

AddressDrilldown.propTypes = {
	open: PropTypes.bool.isRequired,
	filter: PropTypes.object.isRequired,
	config: PropTypes.object.isRequired,
	onChange: PropTypes.func.isRequired,
	selectData: PropTypes.object.isRequired
};
